import {
  Element,
  Matcher,
  MatcherPattern,
  UpcastDispatcher,
  UpcastElementEvent,
  ViewElement,
} from "ckeditor5";

type ViewMatcher = {
  name: string;
  classes?: string[];
  attributes?: string[];
  styles?: string[];
};

/**
 * Creates an upcast that unwraps an element. This is used for example when the
 * downcast for a model element might wrap it in a <figure>, but we need to also
 * handle .
 *
 * For example, to properly unwrap the following view:
 *
 *  <figure class="media">
 *    <video><source .../></video>
 *  </figure>
 *
 * You would create the following upcast:
 *
 *  editor.conversion.for("upcast").add(upcastUnwrapElement(
 *    {
 *      name: "figure",
 *      classes: ["media"]
 *    },
 *    {
 *      name: "video",
 *    }
 *  ));
 *
 * Along with one that handles the actual video element:
 *
 *  editor.conversion.for("upcast").elementToElement({
 *    view: { name: "video" },
 *    model: ...
 *  })
 *
 * This would essentially discard the <figure> element after validating it has the
 * correct classes and children, and then convert the child element directly into
 * the appropriate location in the model.
 *
 * Patterned after
 *  https://github.com/ckeditor/ckeditor5/blob/v40.1.0/packages/ckeditor5-table/src/converters/upcasttable.ts#L28
 *  https://github.com/ckeditor/ckeditor5/blob/v40.1.0/packages/ckeditor5-image/src/image/converters.ts#L40
 *
 * @param viewMatcher the element to unwrap and discard
 * @param viewChildMatcher specification for what the view's child element should be
 *  if omitted, will just convert the first child element
 * @returns
 */
export const upcastUnwrapElement =
  (viewMatcher: ViewMatcher, viewChildMatcher: MatcherPattern) =>
  (dispatcher: UpcastDispatcher) => {
    dispatcher.on<UpcastElementEvent>(
      `element:${viewMatcher.name}`,
      (e, data, api) => {
        const consumableMatcher = { ...viewMatcher, name: true };
        if (!api.consumable.test(data.viewItem, consumableMatcher)) {
          return;
        }

        const children = Array.from(data.viewItem.getChildren());
        const matcher = new Matcher(viewChildMatcher);

        const unwrappedViewElement = children.find(
          (c): c is ViewElement =>
            c.is("view:element") && matcher.match(c) != null,
        );

        if (
          unwrappedViewElement == null ||
          !api.consumable.test(unwrappedViewElement, { name: true })
        ) {
          return;
        }

        api.consumable.consume(data.viewItem, consumableMatcher);

        const conversionResult = api.convertItem(
          unwrappedViewElement,
          data.modelCursor,
        );
        const unwrappedModelElement = Array.from(
          conversionResult.modelRange?.getItems() ?? [],
        ).find((c): c is Element => c.is("element"));

        if (unwrappedModelElement == null) {
          api.consumable.revert(data.viewItem, consumableMatcher);
          return;
        }

        api.convertChildren(
          data.viewItem,
          api.writer.createPositionAt(unwrappedModelElement, "end"),
        );
        api.updateConversionResult(unwrappedModelElement, data);
      },
    );
  };
